import React, { useState } from "react";

import { Trash2 } from "lucide-react";
import { useDispatch } from "react-redux";

import { Button } from "@components/ui/button";

import { useRemoveFile } from "@hooks/mutations/use-files";

import { setMessage } from "@redux/notification";

import { stripBundlePrefix } from "@utils/bundle-name";

import { ConfirmationModal } from "../../ConfirmationModal";
import { FilePreviewData } from "../types/data-table";

type DeleteFileConfirmationProps = {
  file: FilePreviewData;
};

export const DeleteFileConfirmation = ({ file }: DeleteFileConfirmationProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const dispatch = useDispatch();

  const removeFile = useRemoveFile();

  const handleDelete = async () => {
    try {
      await removeFile.mutateAsync(file.id);

      dispatch(
        setMessage({
          content: `File "${stripBundlePrefix(file.name)}" deleted successfully`,
          type: "success",
        })
      );

      setIsOpen(false);
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to delete file";
      dispatch(
        setMessage({
          content: errorMessage,
          type: "error",
        })
      );
    }
  };

  return (
    <ConfirmationModal
      type="warning"
      icon={<Trash2 className="size-14 text-destructive" />}
      title="Delete File"
      description={`Are you sure you want to delete "${stripBundlePrefix(file.name)}"? This action cannot be undone.`}
      confirmButtonText={removeFile.isPending ? "Deleting..." : "Yes, Delete"}
      cancelButtonText="No, Cancel"
      onConfirm={handleDelete}
      isLoading={removeFile.isPending}
      open={isOpen}
      onOpenChange={setIsOpen}
    >
      <Button
        variant="ghost"
        size="sm"
        className="w-auto p-1 text-destructive hover:text-destructive"
      >
        <Trash2 className="size-4" /> Delete File
      </Button>
    </ConfirmationModal>
  );
};
